import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

type MetricsAgency = {
  id: string;
  name: string;
  dealOwner: string | null;
  reportsGenerated: number;
  trialStartDate: string;
  totalUsers: number;
};

type SortColumn = "name" | "dealOwner" | "reportsGenerated" | "trialStartDate" | "totalUsers";
type SortDirection = "asc" | "desc";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * Agency acquisition / sales metrics table. Auth-free: the parent portal
 * supplies the verified `adminEmail` (sent as x-admin-email) and
 * `onUnauthorized` to call when the server rejects the email (403).
 */
function AgencyMetricsBody({
  adminEmail,
  onUnauthorized,
}: {
  adminEmail: string;
  onUnauthorized: () => void;
}) {
  const [agencies, setAgencies] = useState<MetricsAgency[]>([]);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [sortColumn, setSortColumn] = useState<SortColumn>("trialStartDate");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [dataLoading, setDataLoading] = useState<boolean>(false);

  const fetchMetrics = useCallback(
    async (year: number, month: number, isInitialLoad: boolean = false) => {
      setDataLoading(true);
      try {
        const response = await axios.get("/api/agency-management/metrics", {
          params: { year, month },
          headers: { "x-admin-email": adminEmail },
        });

        const years = response.data.availableYears || [];
        if (years.length > 0) {
          setAvailableYears(years);

          // On initial load, if the current year has no data, switch to the most recent year.
          if (isInitialLoad && !years.includes(year)) {
            const mostRecentYear = Math.max(...years);
            setSelectedYear(mostRecentYear);
            const refetch = await axios.get("/api/agency-management/metrics", {
              params: { year: mostRecentYear, month },
              headers: { "x-admin-email": adminEmail },
            });
            setAgencies(refetch.data.agencies);
            setDataLoading(false);
            return;
          }
        }

        setAgencies(response.data.agencies);
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 403) {
          onUnauthorized();
        }
      } finally {
        setDataLoading(false);
      }
    },
    [adminEmail, onUnauthorized],
  );

  useEffect(() => {
    fetchMetrics(selectedYear, selectedMonth, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMetrics]);

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    fetchMetrics(year, selectedMonth);
  };

  const handleMonthChange = (month: number) => {
    setSelectedMonth(month);
    fetchMetrics(selectedYear, month);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedAgencies = useMemo(() => {
    const sorted = [...agencies].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case "name":
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case "dealOwner":
          aValue = (a.dealOwner || "").toLowerCase();
          bValue = (b.dealOwner || "").toLowerCase();
          break;
        case "reportsGenerated":
          aValue = a.reportsGenerated;
          bValue = b.reportsGenerated;
          break;
        case "trialStartDate":
          aValue = a.trialStartDate ? new Date(a.trialStartDate).getTime() : 0;
          bValue = b.trialStartDate ? new Date(b.trialStartDate).getTime() : 0;
          break;
        case "totalUsers":
          aValue = a.totalUsers;
          bValue = b.totalUsers;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [agencies, sortColumn, sortDirection]);

  const totals = useMemo(() => {
    return {
      totalAgencies: agencies.length,
      totalReports: agencies.reduce((sum, a) => sum + a.reportsGenerated, 0),
      totalUsers: agencies.reduce((sum, a) => sum + a.totalUsers, 0),
    };
  }, [agencies]);

  const SortableHeader = ({ column, label }: { column: SortColumn; label: string }) => (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-base-content/60 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-base-300 relative"
      onClick={() => handleSort(column)}
    >
      <div className="pr-6">{label}</div>
      {sortColumn === column && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          {sortDirection === "asc" ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
        </div>
      )}
    </th>
  );

  return (
    <div className="bg-base-100 rounded-xl shadow-md dark:border dark:border-base-300">
      <div className="p-6 border-b border-base-300">
        <h2 className="text-xl font-bold text-base-content">Agency acquisitions</h2>
        <p className="text-gray-600 dark:text-base-content/70 mt-1 text-sm">Departments created, reports generated, and seats by month.</p>
      </div>

      {/* Year Tabs */}
      <div className="border-b border-base-300 bg-base-200 px-6 pt-4">
        <div className="flex gap-2 flex-wrap">
          {availableYears.map((year) => (
            <button
              key={year}
              onClick={() => handleYearChange(year)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                selectedYear === year
                  ? "bg-base-100 text-blue-600 dark:text-blue-300 border-t border-l border-r border-base-300"
                  : "text-base-content/60 hover:text-gray-700 dark:hover:text-base-content/80 hover:bg-gray-100 dark:hover:bg-base-300"
              }`}
            >
              {year}
            </button>
          ))}
        </div>
      </div>

      {/* Month Tabs */}
      <div className="border-b border-base-300 bg-base-100 px-6 py-3">
        <div className="flex gap-1 flex-wrap">
          {MONTHS.map((month, index) => (
            <button
              key={month}
              onClick={() => handleMonthChange(index + 1)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                selectedMonth === index + 1
                  ? "bg-blue-600 text-white"
                  : "text-base-content/60 hover:text-gray-700 dark:hover:text-base-content/80 hover:bg-gray-100 dark:hover:bg-base-200"
              }`}
            >
              {month.substring(0, 3)}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="bg-blue-50 dark:bg-blue-900/40 px-6 py-4 border-b border-base-300">
        <div className="flex gap-8">
          <div>
            <p className="text-xs text-base-content/60 uppercase">Agencies</p>
            <p className="text-2xl font-bold text-base-content">{totals.totalAgencies}</p>
          </div>
          <div>
            <p className="text-xs text-base-content/60 uppercase">Total Reports</p>
            <p className="text-2xl font-bold text-base-content">{totals.totalReports}</p>
          </div>
          <div>
            <p className="text-xs text-base-content/60 uppercase">Total Users</p>
            <p className="text-2xl font-bold text-base-content">{totals.totalUsers}</p>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {dataLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : sortedAgencies.length === 0 ? (
          <div className="text-center py-12 text-base-content/60">
            No agencies created in {MONTHS[selectedMonth - 1]} {selectedYear}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-base-200 border-b border-base-300">
              <tr>
                <SortableHeader column="name" label="Department Name" />
                <SortableHeader column="dealOwner" label="Deal Owner" />
                <SortableHeader column="reportsGenerated" label="Reports Generated" />
                <SortableHeader column="trialStartDate" label="Trial Start Date" />
                <SortableHeader column="totalUsers" label="Total Users" />
              </tr>
            </thead>
            <tbody className="bg-base-100 divide-y divide-base-300">
              {sortedAgencies.map((agency) => (
                <tr key={agency.id} className="hover:bg-base-200">
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-base-content">{agency.name}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-base-content/60">{agency.dealOwner || "—"}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-base-content/60">{agency.reportsGenerated}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-base-content/60">{formatDate(agency.trialStartDate)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-base-content/60">{agency.totalUsers}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default AgencyMetricsBody;
